import React from "react";
import {
  Field,
  Switch,
  Input,
  Button,
  Text,
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Divider,
  Textarea,
} from "@fluentui/react-components";
import { Add20Filled, Delete20Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";

import QueueSelector from "../common/QueueSelector";
import MultilingualText from "../common/MultilingualText";
import MultilingualKeywords from "../common/MultilingualKeywords";
import EditableStringList from "../common/EditableStringList";
import "../../CSS/IaAgent.css";

const emptyRoute = () => ({
  queueId: "",
  queueName: "",
  intent: "",
  description: "",
  examples: [],
  keywords: { en: [], fr: [] },
  clarifyingQuestion: { en: "", fr: "" },
  announcement: { en: "", fr: "" },
});

export default function RoutingTab({ agentData, updateField }) {
  const { t } = useTranslation();
  const qualif = agentData.skills?.callQualification || {};
  const routes = Array.isArray(qualif.routes) ? qualif.routes : [];
  const fallback = qualif.fallback || { behavior: "ask_clarification", disambiguationMessage: {}, ifStillUnclear: {} };

  const setRoute = (index, patch) => {
    const next = routes.slice();
    next[index] = { ...next[index], ...patch };
    updateField("skills.callQualification.routes", next);
  };

  const addRoute = () => {
    updateField("skills.callQualification.routes", [...routes, emptyRoute()]);
  };

  const removeRoute = (index) => {
    const next = routes.slice();
    next.splice(index, 1);
    updateField("skills.callQualification.routes", next);
  };

  return (
    <div className="ia-agent-tab">
      <Switch
        label={t("CALL_QUALIFICATION_ENABLED")}
        checked={!!qualif.enabled}
        onChange={(_, d) => updateField("skills.callQualification.enabled", d.checked)}
      />

      {qualif.enabled && (
        <>
          <div style={{ display: "flex", gap: 16, marginTop: 12 }}>
            <Field label={t("MAX_CLARIFYING_QUESTIONS")} className="field-style" style={{ flex: 1 }}>
              <Input
                type="number"
                min={0}
                max={3}
                value={String(qualif.maxClarifyingQuestions ?? 1)}
                onChange={(_, d) => updateField("skills.callQualification.maxClarifyingQuestions", Number(d.value) || 0)}
              />
            </Field>
            <Field label=" " className="field-style" style={{ flex: 1 }}>
              <Switch
                label={t("CLARIFY_BEFORE_ROUTING")}
                checked={!!qualif.clarifyBeforeRouting}
                onChange={(_, d) => updateField("skills.callQualification.clarifyBeforeRouting", d.checked)}
              />
            </Field>
          </div>

          <Divider style={{ margin: "16px 0" }} />

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <Text size={500} weight="semibold">{t("ROUTES")}</Text>
            <Button appearance="primary" icon={<Add20Filled />} onClick={addRoute}>
              {t("ADD_ROUTE")}
            </Button>
          </div>

          {routes.length === 0 && (
            <Text size={200} style={{ color: "#666" }}>{t("NO_ROUTES_YET")}</Text>
          )}

          <Accordion collapsible multiple>
            {routes.map((route, idx) => (
              <AccordionItem value={`route-${idx}`} key={idx}>
                <AccordionHeader>
                  <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                    <span>
                      {route.queueName || route.intent || t("ROUTE")} #{idx + 1}
                    </span>
                    <Button
                      appearance="subtle"
                      icon={<Delete20Regular />}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeRoute(idx);
                      }}
                      title={t("DELETE")}
                    />
                  </div>
                </AccordionHeader>
                <AccordionPanel>
                  <div style={{ padding: 8 }}>
                    <QueueSelector
                      label={t("QUEUE")}
                      value={route.queueId}
                      onChange={({ queueId, queueName }) => setRoute(idx, { queueId, queueName })}
                    />

                    <Field label={t("INTENT")} className="field-style" required>
                      <Input
                        value={route.intent || ""}
                        onChange={(_, d) => setRoute(idx, { intent: d.value })}
                        placeholder="technical_support"
                      />
                    </Field>

                    <Field label={t("DESCRIPTION")} className="field-style">
                      <Textarea
                        value={route.description || ""}
                        onChange={(_, d) => setRoute(idx, { description: d.value })}
                        placeholder={t("ROUTE_DESCRIPTION_PLACEHOLDER")}
                        rows={2}
                      />
                    </Field>

                    <EditableStringList
                      label={t("EXAMPLES")}
                      value={route.examples || []}
                      onChange={(list) => setRoute(idx, { examples: list })}
                      placeholder={t("EXAMPLE_PLACEHOLDER")}
                    />

                    <MultilingualKeywords
                      label={t("KEYWORDS")}
                      value={route.keywords}
                      onChange={(v) => setRoute(idx, { keywords: v })}
                    />

                    <MultilingualText
                      label={t("ANNOUNCEMENT")}
                      value={route.announcement}
                      onChange={(v) => setRoute(idx, { announcement: v })}
                      placeholder={t("ANNOUNCEMENT_PLACEHOLDER")}
                    />

                    <MultilingualText
                      label={t("CLARIFYING_QUESTION")}
                      value={route.clarifyingQuestion}
                      onChange={(v) => setRoute(idx, { clarifyingQuestion: v })}
                      placeholder={t("CLARIFYING_QUESTION_PLACEHOLDER")}
                    />
                  </div>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>

          <Divider style={{ margin: "16px 0" }} />

          <Text size={500} weight="semibold" style={{ display: "block", marginBottom: 8 }}>
            {t("FALLBACK")}
          </Text>

          <QueueSelector
            label={t("FALLBACK_QUEUE")}
            value={fallback.ifStillUnclear?.queueId}
            onChange={({ queueId, queueName }) =>
              updateField("skills.callQualification.fallback", {
                ...fallback,
                ifStillUnclear: { ...(fallback.ifStillUnclear || {}), queueId, queueName },
              })
            }
          />

          <MultilingualText
            label={t("DISAMBIGUATION_MESSAGE")}
            value={fallback.disambiguationMessage}
            onChange={(v) =>
              updateField("skills.callQualification.fallback", {
                ...fallback,
                disambiguationMessage: v,
              })
            }
            multiline
            placeholder={t("DISAMBIGUATION_MESSAGE_PLACEHOLDER")}
          />
        </>
      )}

      {!qualif.enabled && (
        <Text size={200} style={{ color: "#666", display: "block", marginTop: 12 }}>
          {t("CALL_QUALIFICATION_DISABLED_HINT")}
        </Text>
      )}
    </div>
  );
}
